export interface AccordionNode extends HTMLElement {
  instance: Accordion
}
/**
 * Класс для управления аккордеоном - раскрывающимися секциями контента.
 * Обрабатывает клики по элементам с атрибутом data-accordion-toggle
 * и управляет состоянием активности элементов аккордеона.
 */
export class Accordion {
  private container: AccordionNode

  /**
   * Создает экземпляр аккордеона.
   * @param {HTMLElement} container - Корневой элемент аккордеона с атрибутом data-accordion
   */
  constructor(container: HTMLElement) {
    this.container = container as AccordionNode
    this.container.instance = this
    this.init()
  }

  /**
   * Инициализирует обработчики событий для аккордеона.
   * Устанавливает слушатель события клика на контейнере.
   * @private
   */
  private init() {
    this.container.addEventListener('click', this.onClick)
  }

  /**
   * Обрабатывает клики внутри контейнера аккордеона.
   * Проверяет, произошел ли клик по элементу-переключателю (data-accordion-toggle),
   * и если да, то переключает состояние соответствующего элемента аккордеона.
   * @param {MouseEvent} event - Событие клика мыши
   * @private
   */
  private onClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement
    const toggle = target.closest('[data-accordion-toggle]') as HTMLElement

    if (!toggle) return

    const item = toggle.closest('[data-accordion-item]') as HTMLElement
    this.toggleItem(item, toggle)
  }

  /**
   * Переключает состояние элемента аккордеона между активным и неактивным.
   * Добавляет или удаляет класс 'is-active' и обновляет атрибут aria-expanded
   * для обеспечения доступности.
   * @param {HTMLElement} item - Элемент аккордеона, у которого переключается состояние
   * @param {HTMLElement} toggle - Элемент-переключатель, по которому произошел клик
   * @private
   */
  private toggleItem(item: HTMLElement, toggle: HTMLElement) {
    const isActive = item.classList.contains('is-active')

    if (isActive) {
      item.classList.remove('is-active')
      toggle.setAttribute('aria-expanded', 'false')
    } else {
      item.classList.add('is-active')
      toggle.setAttribute('aria-expanded', 'true')
    }
  }

  public closeAll() {
    const items = this.container.querySelectorAll<HTMLElement>(
      '[data-accordion-item].is-active'
    )
    items.forEach((item) => {
      const toggle = item.querySelector<HTMLElement>('[data-accordion-toggle]')
      if (toggle) {
        item.classList.remove('is-active')
        toggle.setAttribute('aria-expanded', 'false')
      }
    })
  }
}

/**
 * Инициализирует все аккордеоны на странице.
 * Находит все элементы с атрибутом data-accordion и создает
 * для каждого из них экземпляр класса Accordion.
 */
export function initAccordions() {
  const accordions = document.querySelectorAll<HTMLElement>('[data-accordion]')

  accordions.forEach((container) => {
    new Accordion(container)
  })
}
